import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import axios from 'axios';
import { FilePlus, ArrowLeft, Mail, DollarSign, Calendar, Upload, FileText, X, AlertCircle } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const ContractForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [installmentMode, setInstallmentMode] = useState('monthly'); // 'monthly' ou 'custom'
  
  const [formData, setFormData] = useState({
    client_email: searchParams.get('client') || '',
    description: '',
    total_value: '',
    installments: 1,
    installment_value: '',
    due_dates: [''],
    // Campos para modo mensal
    start_date: '',
    payment_day: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    // Auto-calculate installment value
    if (formData.total_value && formData.installments > 0) {
      const installmentValue = parseFloat(formData.total_value) / formData.installments;
      setFormData(prev => ({ ...prev, installment_value: installmentValue.toFixed(2) }));
    }
  }, [formData.total_value, formData.installments]);

  useEffect(() => {
    // Gerar datas automaticamente no modo mensal
    if (installmentMode === 'monthly' && formData.start_date && formData.installments > 0) {
      generateMonthlyDueDates();
    }
  }, [installmentMode, formData.start_date, formData.installments]);

  useEffect(() => {
    // Ajustar tamanho do array de datas no modo personalizado
    if (installmentMode === 'custom') {
      const newDueDates = [...formData.due_dates];
      while (newDueDates.length < formData.installments) {
        newDueDates.push('');
      }
      while (newDueDates.length > formData.installments) {
        newDueDates.pop();
      }
      setFormData(prev => ({ ...prev, due_dates: newDueDates }));
    }
  }, [formData.installments, installmentMode]);

  const generateMonthlyDueDates = () => {
    if (!formData.start_date || formData.installments < 1) return;
    
    const startDate = new Date(formData.start_date + 'T12:00:00');
    const dueDates = [];
    
    for (let i = 0; i < formData.installments; i++) {
      const dueDate = new Date(startDate);
      dueDate.setMonth(dueDate.getMonth() + i);
      
      // Formatar como YYYY-MM-DD
      const year = dueDate.getFullYear();
      const month = String(dueDate.getMonth() + 1).padStart(2, '0');
      const day = String(dueDate.getDate()).padStart(2, '0');
      dueDates.push(`${year}-${month}-${day}`);
    }
    
    setFormData(prev => ({ ...prev, due_dates: dueDates }));
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleDueDateChange = (index, value) => {
    const newDueDates = [...formData.due_dates];
    newDueDates[index] = value;
    setFormData({ ...formData, due_dates: newDueDates });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setError('Tipo de arquivo não permitido. Use PDF ou DOC/DOCX.');
        return;
      }
      // Validar tamanho (máx 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Arquivo muito grande. Máximo 10MB.');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validações
    if (!selectedFile) {
      setError('Por favor, selecione um arquivo de contrato.');
      return;
    }

    if (formData.due_dates.some(date => !date)) {
      setError('Por favor, preencha todas as datas de vencimento.');
      return;
    }

    setLoading(true);

    try {
      // Criar FormData para envio com arquivo
      const submitData = new FormData();
      submitData.append('client_email', formData.client_email);
      submitData.append('description', formData.description);
      submitData.append('total_value', parseFloat(formData.total_value));
      submitData.append('installments', parseInt(formData.installments));
      submitData.append('installment_value', parseFloat(formData.installment_value));
      submitData.append('due_dates', JSON.stringify(formData.due_dates));
      submitData.append('installment_mode', installmentMode);
      submitData.append('status', 'pending');
      submitData.append('contract_file', selectedFile);

      await axios.post(`${API}/admin/contracts`, submitData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSuccess('Contrato criado com sucesso!');
      setTimeout(() => navigate('/admin/contracts'), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar contrato');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/contracts')}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ArrowLeft size={24} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FilePlus className="text-arcano-blue" />
            Novo Contrato
          </h1>
          <p className="text-gray-600">Crie um novo contrato para um cliente</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6 flex items-center gap-2">
            <AlertCircle size={20} />
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md mb-6">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Cliente */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cliente *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <select
                value={formData.client_email}
                onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent appearance-none"
              >
                <option value="">Selecione um cliente</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.email}>
                    {client.full_name} ({client.email})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Descrição do Contrato */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Descrição do Contrato</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
              placeholder="Ex: Desenvolvimento de Sistema Web, Consultoria TI..."
            />
            <p className="text-xs text-gray-500 mt-1">Nome ou descrição para identificar este contrato</p>
          </div>

          {/* Upload de Arquivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Arquivo do Contrato *</label>
            {!selectedFile ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-arcano-blue transition-colors">
                <Upload className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600 mb-2">Arraste um arquivo ou clique para selecionar</p>
                <p className="text-gray-400 text-sm mb-4">PDF, DOC ou DOCX (máx. 10MB)</p>
                <label className="cursor-pointer inline-flex items-center gap-2 bg-arcano-blue text-white px-4 py-2 rounded-md hover:bg-arcano-blue-dark transition-colors">
                  <Upload size={18} />
                  Selecionar Arquivo
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              </div>
            ) : (
              <div className="border border-gray-300 rounded-lg p-4 flex items-center justify-between bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-arcano-blue/10 rounded-lg flex items-center justify-center">
                    <FileText className="text-arcano-blue" size={24} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
            )}
          </div>

          {/* Valores */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Valor Total *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.total_value}
                  onChange={(e) => setFormData({ ...formData, total_value: e.target.value })}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Número de Parcelas *</label>
              <input
                type="number"
                min="1"
                max="48"
                value={formData.installments}
                onChange={(e) => setFormData({ ...formData, installments: parseInt(e.target.value) || 1 })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Valor por Parcela</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="number"
                step="0.01"
                value={formData.installment_value}
                readOnly
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Calculado automaticamente</p>
          </div>

          {/* Modo de Parcelas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Periodicidade das Parcelas *</label>
            <div className="flex gap-4 mb-4">
              <button
                type="button"
                onClick={() => setInstallmentMode('monthly')}
                className={`flex-1 py-3 px-4 rounded-md border-2 transition-all ${
                  installmentMode === 'monthly'
                    ? 'border-arcano-blue bg-arcano-blue/5 text-arcano-blue font-medium'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                }`}
              >
                <Calendar className="inline-block mr-2 mb-1" size={18} />
                Mensal
              </button>
              <button
                type="button"
                onClick={() => setInstallmentMode('custom')}
                className={`flex-1 py-3 px-4 rounded-md border-2 transition-all ${
                  installmentMode === 'custom'
                    ? 'border-arcano-blue bg-arcano-blue/5 text-arcano-blue font-medium'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                }`}
              >
                <Calendar className="inline-block mr-2 mb-1" size={18} />
                Personalizado
              </button>
            </div>

            {installmentMode === 'monthly' ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-700 mb-4">
                  As parcelas serão geradas automaticamente a partir da data inicial, com vencimento mensal.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data da Primeira Parcela *</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    />
                  </div>
                </div>
                
                {formData.due_dates.length > 0 && formData.due_dates[0] && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Prévia das datas de vencimento:</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {formData.due_dates.map((date, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-600">
                          <span className="w-20">Parcela {index + 1}:</span>
                          <span>{new Date(date + 'T12:00:00').toLocaleDateString('pt-BR')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-500 mb-3">
                  Defina manualmente a data de vencimento de cada parcela.
                </p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {formData.due_dates.map((date, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 w-24">Parcela {index + 1}:</span>
                      <div className="relative flex-1">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="date"
                          value={date}
                          onChange={(e) => handleDueDateChange(index, e.target.value)}
                          required
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/admin/contracts')}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-arcano-blue text-white px-4 py-3 rounded-md hover:bg-arcano-blue-dark transition-colors disabled:opacity-50"
            >
              {loading ? 'Criando...' : 'Criar Contrato'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContractForm;
